import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Spinner,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { fetchUserReminders } from './services/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return dateString;
};

const MyLibrary = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadReminders = async () => {
      setLoading(true);
      try {
        const userReminders = await fetchUserReminders();
        setReminders(userReminders);
        setError(null);
      } catch (err) {
        console.error('Failed to load reminders:', err);
        setError('Failed to load your calibration reminders. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadReminders();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <Heading as="h1" size="xl">
        My Calibration Library
      </Heading>
      <Text>
        Keep track of all your calibration activities and reminders. This section helps you stay on top of your calibration schedule, ensuring your devices are always performing at their best.
      </Text>

      {/* Calibration Reminders Table */}
      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.300">
        <Heading as="h2" size="md" mb={3}>Calibration Reminders</Heading>
        <Text mb={4}>
          Here are your upcoming calibration reminders. Keeping a regular calibration schedule ensures your devices maintain optimal color accuracy.
        </Text>
        
        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <TableContainer>
            <Table colorScheme="simple">
              <Thead>
                <Tr>
                  <Th>Device</Th>
                  <Th>Serial Number</Th>
                  <Th>Machine</Th>
                  <Th>Monitor</Th>
                  <Th>Due Date</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {reminders.length > 0 ? (
                  reminders.map((reminder, index) => (
                    <Tr key={index}>
                      <Td>{reminder.calibrationType || 'Unknown'}</Td>
                      <Td>{reminder.serial}</Td>
                      <Td>{reminder.machineHint || 'N/A'}</Td>
                      <Td>{reminder.monitorHint || 'N/A'}</Td>
                      <Td>{formatDate(reminder.date)}</Td>
                      <Td>
                        <Badge 
                          colorScheme={reminder.reminderSent ? "red" : "green"}
                        >
                          {reminder.reminderSent ? "Due Soon" : "Up to Date"}
                        </Badge>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={4}>
                      <Text>No calibration reminders set up yet.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Calibration Benefits */}
      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.300">
        <Heading as="h2" size="md" mb={3}>Benefits of Regular Calibration</Heading>
        <VStack align="start" spacing={3}>
          <Text>
            <strong>✓ Consistent color accuracy</strong> - Maintain precise color representation across all your devices
          </Text>
          <Text>
            <strong>✓ Professional results</strong> - Ensure your work meets industry standards
          </Text>
          <Text>
            <strong>✓ Extended device life</strong> - Regular calibration helps identify issues before they become problems
          </Text>
          <Text>
            <strong>✓ Workflow efficiency</strong> - Spend less time correcting color issues in post-production
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
};

export default MyLibrary;